import {snakeCase} from 'lodash';
import contentStudioInstance from '@/utils/axiosContentStudio';
import mapKeysToCase from '@/utils/mapKeysToCase';
import type {
  CreateDraftProjectParams,
  BaseProjectResponse,
  UpdateDraftProjectParams,
  UpdateDraftProjectResponse,
  UpdateProjectPatchParams,
} from './types';

/**
 * Create a new draft project.
 *
 * HTTP POST `/api/v1/projects`
 */
const createDraftProject = async (
  params: CreateDraftProjectParams
): Promise<BaseProjectResponse> => {
  const snakeCaseParams = mapKeysToCase(params, snakeCase);
  const response = await contentStudioInstance.post(
    '/api/v1/projects',
    snakeCaseParams
  );
  return response.data;
};

/**
 * Update an existing draft project with the given projectId by replacing its fields.
 *
 * HTTP PUT `/api/v1/projects/{projectId}`
 */
const updateDraftProject = async (
  projectId: string,
  params: UpdateDraftProjectParams
): Promise<UpdateDraftProjectResponse> => {
  const snakeCaseParams = mapKeysToCase(params, snakeCase);
  const response = await contentStudioInstance.put(
    `/api/v1/projects/${projectId}`,
    snakeCaseParams
  );
  return response.data;
};

/**
 * Update fields of an existing project with the given projectId.
 *
 * HTTP PATCH `/api/v1/projects/{projectId}`
 *
 * Currently supports updating the project name only.
 */
const updateProjectPatch = async (
  projectId: string,
  params: UpdateProjectPatchParams
): Promise<void> => {
  const snakeCaseParams = mapKeysToCase(params, snakeCase);
  await contentStudioInstance.patch(
    `/api/v1/projects/${projectId}`,
    snakeCaseParams
  );
};

/**
 * Delete the project with the given projectId.
 *
 * HTTP DELETE `/api/v1/projects/{projectId}`
 */
const deleteProject = async (projectId: string): Promise<void> => {
  await contentStudioInstance.delete(`/api/v1/projects/${projectId}`);
};

const projectUpdaters = {
  createDraftProject,
  updateDraftProject,
  updateProjectPatch,
  deleteProject,
};

export default projectUpdaters;
